import * as React from 'react';
import './statusBar.scss';
import { Metadata, Schema, SchemaField } from '../../../types/Metadata';
import { useActions } from '.././metadataFormActions';
import { GoogleDoc, Home, Trash } from '../../../../src/components/Icon/icon';
import { GIcon } from '../../../../src/components/Icon/icon';
import { DocSearch } from '../../../../src/components/Icon/icon';
import { StatusField } from '../StatusField/StatusField';
import { useConfirmModal } from '../../ConfirmModal/confirmModal';
import AccessControl from '../../AccessControl/accessControl';
import { useLoader } from '../../Loader/loader';
import { getValidationErrors } from '../FieldGroup/fieldGroup';
import {
  findOneByProps,
  getConfPresentationLink,
  getFieldValue,
  getItemStatusId,
  getItemType,
  getSchemaForm
} from '../../../utils';
import { ItemStatus } from '../../../enums/ItemStatus';
import { updateMetadataFormState } from '../metadataFormReducer';
import { useFullStory } from '../../App/fullStory';
import { FSEvent } from '../../../enums/FSEvent';
import { ItemActions } from '../ItemActions/itemActions';
import { Icon, Presentation } from 'rcd-cpp-ui/ui/icon';
import { Resource } from '../../../enums/Resource';
import { useSelector } from '../../../utils/useSelector';
import { Approvals } from '../Approvals/approvals';
import { ItemType } from '../../../enums/ItemType';
import TooltipPopover from '../../Popover/popover';
import { useNavigate } from 'react-router-dom';
import URLManager from '../../../utils/urlManager';
import { NodeType } from '../../../enums/NodeType';
import { ValidationMode } from '../../../enums/ValidationMode';
import { FormUpdater } from '../../../types/Field';

interface StatusBarProps {
  data?: Partial<Metadata>;
  fields?: SchemaField[];
  updater?: FormUpdater;
}

const isDemotingDocument = (
  oldStatusId: number,
  newStatusId: number
): boolean => {
  if (newStatusId == ItemStatus.Not_Pursued) {
    return true;
  } else if (
    oldStatusId == ItemStatus.Concept_Review &&
    [ItemStatus.Idea].includes(newStatusId)
  ) {
    return true;
  }
  return false;
};

const getFields = (schema: Schema) =>
  (schema?.props?.fields || []).reduce(
    (fields, field) => {
      switch (field.key) {
        case 'Item.status':
          return { ...fields, statusField: field };
        case 'Item.inThePlan':
          return { ...fields, inThePlanField: field };
        case 'Item.critical':
          return { ...fields, criticalField: field };
        case 'Item.evolvingInsights':
          return { ...fields, evolvingInsightsField: field };
        case 'Item.requestWriterHelp':
          return { ...fields, requestWriterHelpField: field };
        case 'Item.requestCreativeHelp':
          return { ...fields, requestCreativeHelp: field };
        case 'Item.bigRocks':
          return { ...fields, bigRocksField: field };
      }
      return fields;
    },
    {
      statusField: null,
      inThePlanField: null,
      criticalField: null,
      evolvingInsightsField: null,
      requestWriterHelpField: null,
      requestCreativeHelp: null,
      bigRocksField: null
    }
  );

export const StatusBar = ({ updater }: StatusBarProps) => {
  const [updatingStatus, setUpdatingStatus] = React.useState(false);
  const data = useSelector(s => s.form?.data);
  const schema = useSelector(s => s.form?.schema);
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const w = useSelector(s =>
    findOneByProps(s.planning?.workspaces, {
      refId: data?.props?.homeWorkspaceRefId
    })
  );
  const {
    showForm,
    pushToCPP,
    updateShouldValidate,
    getAutoAcceptImpliedDataProps
  } = useActions();
  const navigate = useNavigate();

  const { captureEvent } = useFullStory();
  const { isLoading, pushToCPP: _pushToCPP } = useLoader({ pushToCPP });
  const schemaForm = getSchemaForm(schema, data);
  const fields = schemaForm?.props?.fields;
  const {
    statusField,
    inThePlanField,
    criticalField,
    evolvingInsightsField,
    requestWriterHelpField,
    requestCreativeHelp,
    bigRocksField
  } = getFields(schema);
  const { updateField, update, updateFields } = updater;
  const { confirm, ConfirmModal: DeleteModal } = useConfirmModal({
    label: 'Delete'
  });
  const disabled = isLoading;
  const url = URLManager.generateUrl(w, { id: data?.props?.refId });
  return (
    <>
      <div className="slideout-status-field">
        {statusField && (
          <StatusField
            key={statusField.field}
            field={statusField}
            data={data}
            schema={schema}
            onChange={async (newStatus, oldStatus) => {
              const impliedDataProps = await getAutoAcceptImpliedDataProps(
                data,
                schemaForm
              );
              const invalidFields = fields
                .map(field => {
                  const value = getFieldValue(field, data);
                  return getValidationErrors({
                    field,
                    data,
                    value,
                    impliedDataProps,
                    shouldValidate:
                      oldStatus?.props?.id == ItemStatus.Not_Pursued &&
                      newStatus?.props?.id == ItemStatus.Idea
                        ? ValidationMode.Current
                        : ValidationMode.Next
                  });
                })
                ?.filter(x => x);
              if (
                invalidFields?.length &&
                !isDemotingDocument(oldStatus?.props?.id, newStatus?.props?.id)
              ) {
                updateShouldValidate(ValidationMode.Next);
                return;
              } else {
                updateShouldValidate(ValidationMode.None);
                if (newStatus?.props?.id == ItemStatus.Not_Pursued) {
                  updateFields([
                    { field: statusField, value: [newStatus] },
                    { field: inThePlanField, value: false },
                    { field: criticalField, value: false },
                    { field: requestWriterHelpField, value: false },
                    { field: requestCreativeHelp, value: false },
                    { field: bigRocksField, value: false }
                  ]);
                } else {
                  updateField(statusField, [newStatus]);
                }

                updateMetadataFormState({
                  pushResponse: null
                });
                setUpdatingStatus(true);
                update()
                  .catch(e => {
                    updateField(statusField, [oldStatus]);
                  })
                  .finally(() => {
                    setUpdatingStatus(false);
                    captureEvent(FSEvent.UpdateItemStatus, {
                      fromStatusId: oldStatus?.props?.id,
                      toStatusId: newStatus?.props?.id,
                      fromStatusText: oldStatus?.props?.description,
                      toStatusText: newStatus?.props?.description
                    });
                  });
              }
            }}
          />
        )}
      </div>
      <div className="link-container">
        <Approvals
          updatingStatus={updatingStatus}
          pushToCPP={_pushToCPP}
          disabled={disabled}
        />
        <AccessControl resource={Resource.itemActionsMenu} data={data}>
          <ItemActions disabled={disabled} />
        </AccessControl>
        <GoogleDocLink url={data?.props?.googleDocumentUrl} />
        <PresentationLink url={getConfPresentationLink(data)} />
        <CPPLink data={data} />
        <GComLink data={data} />
        <AccessControl resource={Resource.removeItem} data={data}>
          <Icon
            disabled={disabled}
            onClick={() => {
              confirm(
                <>
                  Are you sure you want to remove this idea:&nbsp;
                  <b>{data?.props?.title}</b>?
                </>,
                {
                  header: 'Remove Idea',
                  onDelete: () => {
                    const removeStatus = {
                      labels: ['Status'],
                      props: {
                        description: 'Removed',
                        id: 6,
                        refId: 'status:6'
                      },
                      rsLabel: 'Removed'
                    };
                    updateFields([
                      { field: statusField, value: [removeStatus] }
                    ]);
                    update().finally(() => {
                      showForm(data, schema);
                    });
                  }
                }
              );
            }}
          >
            <Trash />
          </Icon>
        </AccessControl>
        {!!w && schema?.props?.type == NodeType.Item && (
          <TooltipPopover
            children={
              selectedWorkspace?.props?.refId != w?.props?.refId ? (
                <a
                  href={url}
                  onClick={e => {
                    e.preventDefault();
                    navigate(url);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Home />
                </a>
              ) : (
                <Home />
              )
            }
            content={`${w?.props?.name} Workspace`}
            type={'info'}
            packed
            pointerPosition="right"
          />
        )}
      </div>
      <DeleteModal />
    </>
  );
};

const GoogleDocLink = ({ url }) => {
  if (!url) return null;
  return (
    <a className="link" href={url} target="_blank">
      <GoogleDoc />
      Google Doc
    </a>
  );
};

const PresentationLink = ({ url }) => {
  if (!url) return null;
  return (
    <a className="link" href={url} target="_blank">
      <Presentation />
      Presentation
    </a>
  );
};

const CPPLink = ({ data }: { data: Partial<Metadata> }) => {
  const itemId = data?.props?.itemId;
  if (!itemId) return null;
  const itemType = getItemType(data);
  let url = process.env.CPP_DOCUMENT_URL + itemId;
  if (itemType != ItemType.content) {
    url = process.env.CPP_CONFERENCE_URI + '/item/' + itemId;
  }
  return (
    <a className="link" href={url} target="_blank">
      <DocSearch />
      CPP
    </a>
  );
};

const GComLink = ({ data }: { data: Partial<Metadata> }) => {
  const itemId = data?.props?.itemId;
  if (!itemId) return null;
  const itemType = getItemType(data);
  if (itemType != ItemType.content) return null;
  const statusId = getItemStatusId(data);
  if (statusId && (statusId >= 800 || statusId == 8)) {
    return (
      <a
        className="link"
        href={process.env.GCOM_DOCUMENT_URL + itemId}
        target="_blank"
      >
        <GIcon />
        .com
      </a>
    );
  }
  return null;
};
